import React, { useState, useRef, useEffect } from 'react';
import { MenuItem } from '@/types/menu';
import { MenuItemCard } from './MenuItemCard';
import { LanguageCode, MENU_UI_TRANSLATIONS, getTranslatedItem } from '@/constants/menuUITranslations';

interface VirtualMenuSectionProps {
  category: string;
  items: MenuItem[];
  primaryColor?: string | null;
  isHappyHour: boolean;
  currentLanguage?: LanguageCode;
}

const ITEMS_PER_PAGE = 6; // Réduit à 6 items par page
const INTERSECTION_THRESHOLD = 0.1;

const VirtualMenuSection: React.FC<VirtualMenuSectionProps> = ({
  category,
  items,
  primaryColor,
  isHappyHour,
  currentLanguage = 'fr'
}) => {
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Force le chargement des premières images immédiatement
  useEffect(() => {
    const firstItems = items.slice(0, 3).map(item => item.id);
    setLoadedImages(new Set(firstItems));
  }, [items]);

  const hasMore = visibleItems < items.length;
  const displayItems = items.slice(0, visibleItems);

  // Intersection Observer pour le lazy loading des images
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const itemId = (entry.target as HTMLElement).dataset.itemId;
            if (itemId) {
              setLoadedImages(prev => new Set([...prev, itemId]));
            }
          }
        });
      },
      { 
        threshold: INTERSECTION_THRESHOLD,
        rootMargin: '20px' // Réduit la zone de pré-chargement
      }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Observer pour "Load More"
  useEffect(() => {
    const loadMoreObserver = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore) {
          setVisibleItems(prev => Math.min(prev + ITEMS_PER_PAGE, items.length));
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      loadMoreObserver.observe(loadMoreRef.current);
    }

    return () => loadMoreObserver.disconnect();
  }, [hasMore, items.length]);

  // Attacher l'observer aux items
  const itemRef = (element: HTMLDivElement | null, itemId: string) => {
    if (element && observerRef.current) {
      element.dataset.itemId = itemId;
      observerRef.current.observe(element);
    }
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h2 
        id={`category-${category}`}
        className="text-2xl font-bold mb-4 text-gray-800 sticky top-0 bg-white z-10 py-2"
        style={{ color: primaryColor || undefined }}
      >
        {category}
      </h2>
      
      <div className="space-y-4">
        {displayItems.map((item) => {
          const translated = getTranslatedItem(item, currentLanguage);
          return (
            <div
              key={item.id}
              ref={(el) => itemRef(el, item.id)}
              className="min-h-[120px]"
            >
              <MenuItemCard
                item={{ ...item, name: translated.name, description: translated.description || '' }}
                primaryColor={primaryColor}
                isHappyHour={isHappyHour}
                shouldLoadImage={loadedImages.has(item.id)}
              />
            </div>
          );
        })}
      </div>

      {/* Load More trigger */}
      {hasMore && (
        <div ref={loadMoreRef} className="mt-6 flex justify-center">
          <div className="animate-pulse text-muted-foreground text-sm">
            {currentLanguage === 'fr' ? 'Chargement de plus d\'éléments...' : 'Loading more items...'}
          </div>
        </div>
      )}

      {/* Items counter */}
      <div className="mt-4 text-center text-sm text-muted-foreground">
        {Math.min(visibleItems, items.length)} / {items.length} {currentLanguage === 'fr' ? 'éléments affichés' : 'items displayed'}
      </div>
    </div>
  );
};

export default VirtualMenuSection;